/**
 * Operations Projects API Functions
 */

import { api } from '@/lib/api';
import type {
  Project,
  ProjectListItem,
  ProjectsResponse,
  ProjectsStats,
  ProjectsQueryParams,
  CreateProjectInput,
  UpdateProjectInput,
  MilestoneTemplate,
  V4ProjectsResponse,
  V4ProjectListItem,
  V4Project,
  V4StatsResponse,
  V4MilestoneTemplatesResponse,
  ProjectType,
  ProjectStatus,
  Quote,
  QuotesQueryParams,
  QuotesResponse,
  QuoteStatus,
  V4Quote,
  V4QuotesResponse,
  QuoteSku,
  V4QuoteSkusResponse,
} from './types';

// =============================================================================
// Transform Functions
// =============================================================================

function transformV4ProjectListItem(v4: V4ProjectListItem): ProjectListItem {
  return {
    id: v4.id,
    project_number: v4.projectNumber,
    quote_id: v4.quoteId,
    quote_title: v4.quoteTitle || v4.quoteProjectName,
    type: v4.type as ProjectType,
    status: v4.status as ProjectStatus,
    quote_number: v4.quoteNumber,
    project_name: v4.projectName,
    amount: v4.amount ? parseFloat(v4.amount) : undefined,
    currency: v4.currency,
    scheduled_date: v4.scheduledDate,
    scheduled_time_start: v4.scheduledTimeStart,
    scheduled_time_end: v4.scheduledTimeEnd,
    assigned_to_id: v4.assignedToId,
    assigned_to_name: v4.assignedToName,
    milestones: v4.milestones || [],
    city: v4.city,
    created_at: v4.createdAt,
    account_id: v4.accountId,
    account_name: v4.accountName,
    account_number: v4.accountNumber,
    archived_at: v4.archivedAt,
  };
}

function transformV4Project(v4: V4Project): Project {
  return {
    id: v4.id,
    project_number: v4.projectNumber,
    account_id: v4.accountId,
    site_id: v4.siteId,
    quote_id: v4.quoteId,
    mongo_quote_id: v4.mongoQuoteId,
    mongo_project_id: v4.mongoProjectId,
    type: v4.type as ProjectType,
    status: v4.status as ProjectStatus,
    roadblock_reason: v4.roadblockReason,
    quote_number: v4.quoteNumber,
    quote_title: v4.quoteTitle || v4.quoteProjectName,
    project_name: v4.projectName,
    description: v4.description,
    amount: v4.amount ? parseFloat(v4.amount) : undefined,
    currency: v4.currency,
    scheduled_date: v4.scheduledDate,
    scheduled_time_start: v4.scheduledTimeStart,
    scheduled_time_end: v4.scheduledTimeEnd,
    estimated_duration_hours: v4.estimatedDurationHours ? parseFloat(v4.estimatedDurationHours) : undefined,
    scheduled_dates: v4.scheduledDates?.map((d) => ({
      id: d.id,
      date: d.date,
      time: d.time,
      status: d.status as 'proposed' | 'confirmed',
    })),
    contact_name: v4.contactName,
    contact_email: v4.contactEmail,
    contact_phone: v4.contactPhone,
    contact_phone_ext: v4.contactPhoneExt,
    address: v4.address,
    city: v4.city,
    postal_code: v4.postalCode,
    state_province: v4.stateProvince,
    country: v4.country,
    shipping_address: v4.shippingAddress,
    shipping_date: v4.shippingDate,
    courier: v4.courier,
    tracking_number: v4.trackingNumber,
    account_manager_id: v4.accountManagerId,
    account_manager_name: v4.accountManagerName,
    assigned_to_id: v4.assignedToId,
    assigned_to_name: v4.assignedToName,
    installed_by: v4.installedBy as Project['installed_by'],
    milestones: v4.milestones || [],
    skus: v4.skus || [],
    notes: v4.notes,
    internal_notes: v4.internalNotes || [],
    notify_customer: v4.notifyCustomer,
    completed_at: v4.completedAt,
    completed_by: v4.completedBy,
    completed_by_name: v4.completedByName,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
    created_by: v4.createdBy,
    archived_at: v4.archivedAt,
    // Joined fields
    account_name: v4.accountName,
    account_number: v4.accountNumber,
    site_name: v4.siteName,
  };
}

function transformV4MilestoneTemplate(v4: V4MilestoneTemplatesResponse['templates'][0]): MilestoneTemplate {
  return {
    id: v4.id,
    project_type: v4.projectType as ProjectType,
    projectType: v4.projectType as ProjectType,
    name: v4.name,
    description: v4.description,
    milestones: v4.milestones,
    is_default: v4.isDefault,
    is_active: v4.isActive,
  };
}

// Transform input to camelCase for API
function transformCreateInput(input: CreateProjectInput): Record<string, unknown> {
  return {
    type: input.type,
    roadblockReason: input.roadblock_reason,
    quoteNumber: input.quote_number,
    quoteId: input.quote_id,
    projectName: input.project_name,
    description: input.description,
    accountId: input.account_id,
    siteId: input.site_id,
    scheduledDate: input.scheduled_date,
    scheduledTimeStart: input.scheduled_time_start,
    scheduledTimeEnd: input.scheduled_time_end,
    scheduledDates: input.scheduled_dates,
    tasks: input.tasks,
    amount: input.amount,
    currency: input.currency,
    contactName: input.contact_name,
    contactEmail: input.contact_email,
    contactPhone: input.contact_phone,
    contactPhoneExt: input.contact_phone_ext,
    address: input.address,
    city: input.city,
    postalCode: input.postal_code,
    stateProvince: input.state_province,
    country: input.country,
    accountManagerId: input.account_manager_id,
    accountManagerName: input.account_manager_name,
    assignedToId: input.assigned_to_id,
    assignedToName: input.assigned_to_name,
    installedBy: input.installed_by,
    milestones: input.milestones,
    skus: input.skus,
    shippingAddress: input.shipping_address,
    shippingDate: input.shipping_date,
    courier: input.courier,
    trackingNumber: input.tracking_number,
    notes: input.notes,
    status: input.status,
    notifyCustomer: input.notify_customer,
  };
}

function transformUpdateInput(input: UpdateProjectInput): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  if (input.type !== undefined) result.type = input.type;
  if (input.roadblock_reason !== undefined) result.roadblockReason = input.roadblock_reason;
  if (input.quote_number !== undefined) result.quoteNumber = input.quote_number;
  if (input.quote_id !== undefined) result.quoteId = input.quote_id;
  if (input.project_name !== undefined) result.projectName = input.project_name;
  if (input.description !== undefined) result.description = input.description;
  if (input.account_id !== undefined) result.accountId = input.account_id;
  if (input.site_id !== undefined) result.siteId = input.site_id;
  if (input.scheduled_date !== undefined) result.scheduledDate = input.scheduled_date;
  if (input.scheduled_time_start !== undefined) result.scheduledTimeStart = input.scheduled_time_start;
  if (input.scheduled_time_end !== undefined) result.scheduledTimeEnd = input.scheduled_time_end;
  if (input.scheduled_dates !== undefined) result.scheduledDates = input.scheduled_dates;
  if (input.amount !== undefined) result.amount = input.amount;
  if (input.currency !== undefined) result.currency = input.currency;
  if (input.contact_name !== undefined) result.contactName = input.contact_name;
  if (input.contact_email !== undefined) result.contactEmail = input.contact_email;
  if (input.contact_phone !== undefined) result.contactPhone = input.contact_phone;
  if (input.contact_phone_ext !== undefined) result.contactPhoneExt = input.contact_phone_ext;
  if (input.address !== undefined) result.address = input.address;
  if (input.city !== undefined) result.city = input.city;
  if (input.postal_code !== undefined) result.postalCode = input.postal_code;
  if (input.state_province !== undefined) result.stateProvince = input.state_province;
  if (input.country !== undefined) result.country = input.country;
  if (input.account_manager_id !== undefined) result.accountManagerId = input.account_manager_id;
  if (input.account_manager_name !== undefined) result.accountManagerName = input.account_manager_name;
  if (input.assigned_to_id !== undefined) result.assignedToId = input.assigned_to_id;
  if (input.assigned_to_name !== undefined) result.assignedToName = input.assigned_to_name;
  if (input.installed_by !== undefined) result.installedBy = input.installed_by;
  if (input.milestones !== undefined) result.milestones = input.milestones;
  if (input.skus !== undefined) result.skus = input.skus;
  if (input.shipping_address !== undefined) result.shippingAddress = input.shipping_address;
  if (input.shipping_date !== undefined) result.shippingDate = input.shipping_date;
  if (input.courier !== undefined) result.courier = input.courier;
  if (input.tracking_number !== undefined) result.trackingNumber = input.tracking_number;
  if (input.notes !== undefined) result.notes = input.notes;
  if (input.status !== undefined) result.status = input.status;
  if (input.tasks !== undefined) result.tasks = input.tasks;
  if (input.notify_customer !== undefined) result.notifyCustomer = input.notify_customer;
  if (input.archived_at !== undefined) result.archivedAt = input.archived_at;

  return result;
}

// =============================================================================
// API Functions
// =============================================================================

export async function fetchProjects(params: ProjectsQueryParams = {}): Promise<ProjectsResponse> {
  const queryParams: Record<string, string | number | boolean> = {};

  if (params.pageNumber !== undefined) queryParams.pageNumber = params.pageNumber;
  if (params.pageLength !== undefined) queryParams.pageLength = params.pageLength;
  if (params.sortKey) queryParams.sortKey = params.sortKey;
  if (params.sortDirection) queryParams.sortDirection = params.sortDirection;
  if (params.type) queryParams.type = params.type;
  if (params.status) queryParams.status = params.status;
  if (params.accountId) queryParams.accountId = params.accountId;
  if (params.assignedToId) queryParams.assignedToId = params.assignedToId;
  if (params.accountManagerId) queryParams.accountManagerId = params.accountManagerId;
  if (params.scheduledDateFrom) queryParams.scheduledDateFrom = params.scheduledDateFrom;
  if (params.scheduledDateTo) queryParams.scheduledDateTo = params.scheduledDateTo;
  if (params.search) queryParams.search = params.search;
  if (params.includeArchived) queryParams.includeArchived = params.includeArchived;

  const response = await api.get<V4ProjectsResponse>('/v4/operations-projects', queryParams);

  return {
    projects: response.projects.map(transformV4ProjectListItem),
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
    pagination: {
      pageNumber: response.pageNumber,
      pageLength: response.pageLength,
      totalRecords: response.total,
      totalPages: response.pageLength > 0 ? Math.ceil(response.total / response.pageLength) : 0,
    },
  };
}

export async function fetchProject(id: string): Promise<Project> {
  const response = await api.get<V4Project>(`/v4/operations-projects/${id}`);
  return transformV4Project(response);
}

export async function fetchProjectsStats(params: { accountId?: string; type?: ProjectType } = {}): Promise<ProjectsStats> {
  const queryParams: Record<string, string> = {};
  if (params.accountId) queryParams.accountId = params.accountId;
  if (params.type) queryParams.type = params.type;

  const response = await api.get<V4StatsResponse>('/v4/operations-projects/stats', queryParams);
  return {
    totalCount: response.totalCount,
    activeCount: response.activeCount,
    activeProjects: response.activeCount,
    completedCount: response.completedCount,
    totalAmount: response.totalAmount,
    totalInvoiceAmount: response.totalAmount,
    currency: 'CAD', // Default currency (matches backend)
    byType: response.byType,
    nextInstallation: response.nextInstallation,
    nextInstallationDate: response.nextInstallation?.scheduledDate,
  };
}

export async function fetchMilestoneTemplates(projectType?: string): Promise<MilestoneTemplate[]> {
  const queryParams: Record<string, string> = {};
  if (projectType) queryParams.type = projectType;

  const response = await api.get<V4MilestoneTemplatesResponse>('/v4/operations-projects/milestone-templates', queryParams);
  return response.templates.map(transformV4MilestoneTemplate);
}

export async function fetchTakenQuoteNumbers(): Promise<string[]> {
  const response = await api.get<{ quoteNumbers: string[] }>('/v4/operations-projects/taken-quote-numbers');
  return response.quoteNumbers;
}

export async function createProject(input: CreateProjectInput): Promise<Project> {
  const response = await api.post<V4Project>('/v4/operations-projects', transformCreateInput(input));
  return transformV4Project(response);
}

export async function updateProject(id: string, input: UpdateProjectInput): Promise<Project> {
  const response = await api.put<V4Project>(`/v4/operations-projects/${id}`, transformUpdateInput(input));
  return transformV4Project(response);
}

export async function archiveProject(id: string): Promise<{ archived: boolean }> {
  return api.post(`/v4/operations-projects/${id}/archive`);
}

export async function unarchiveProject(id: string): Promise<{ unarchived: boolean }> {
  return api.post(`/v4/operations-projects/${id}/unarchive`);
}

export async function changeProjectStatus(id: string, status: ProjectStatus): Promise<Project> {
  const response = await api.post<V4Project>(`/v4/operations-projects/${id}/status`, { status });
  return transformV4Project(response);
}

export async function completeMilestone(projectId: string, milestoneKey: string): Promise<Project> {
  const response = await api.post<V4Project>(`/v4/operations-projects/${projectId}/milestones/${milestoneKey}/complete`);
  return transformV4Project(response);
}

export async function uncompleteMilestone(projectId: string, milestoneKey: string): Promise<Project> {
  const response = await api.post<V4Project>(`/v4/operations-projects/${projectId}/milestones/${milestoneKey}/uncomplete`);
  return transformV4Project(response);
}

export interface AuditLogEntry {
  id: string;
  project_id: string;
  timestamp: string;
  action: string;
  details?: Record<string, unknown>;
  performed_by?: string;
  performed_by_name?: string;
}

interface V4AuditLogsResponse {
  logs: Array<{
    id: string;
    projectId: string;
    timestamp: string;
    action: string;
    details?: Record<string, unknown>;
    performedBy?: string;
    performedByName?: string;
  }>;
  pageNumber: number;
  pageLength: number;
  total: number;
}

export async function fetchAuditLogs(
  projectId: string,
  params: { pageNumber?: number; pageLength?: number } = {}
): Promise<{ logs: AuditLogEntry[]; pageNumber: number; pageLength: number; total: number }> {
  const queryParams: Record<string, number> = {};
  if (params.pageNumber !== undefined) queryParams.pageNumber = params.pageNumber;
  if (params.pageLength !== undefined) queryParams.pageLength = params.pageLength;

  const response = await api.get<V4AuditLogsResponse>(`/v4/operations-projects/${projectId}/audit-logs`, queryParams);

  return {
    logs: response.logs.map((log) => ({
      id: log.id,
      project_id: log.projectId,
      timestamp: log.timestamp,
      action: log.action,
      details: log.details,
      performed_by: log.performedBy,
      performed_by_name: log.performedByName,
    })),
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
  };
}

// =============================================================================
// Quote API Functions
// =============================================================================

function transformV4Quote(v4: V4Quote): Quote {
  return {
    id: v4.id,
    shipping_address: v4.shippingAddress,
    quote_number: v4.quoteNumber,
    project_name: v4.projectName,
    name: v4.name,
    description: v4.description,
    status: v4.status as QuoteStatus,
    created_date: v4.createdDate,
    expiration_date: v4.expirationDate,
    sent_date: v4.sentDate,
    accepted_date: v4.acceptedDate,
    estimated_delivery: v4.estimatedDelivery,
    subtotal: parseFloat(v4.subtotal) || 0,
    discount: parseFloat(v4.discount) || 0,
    tax_amount: parseFloat(v4.taxAmount) || 0,
    total: parseFloat(v4.total) || 0,
    currency: v4.currency,
    tax_category: v4.taxCategory,
    sales_manager_id: v4.salesManagerId,
    sales_manager_name: v4.salesManagerName,
    contractual_manager_id: v4.contractualManagerId,
    contractual_manager_name: v4.contractualManagerName,
    contractual_manager_email: v4.contractualManagerEmail,
    contractual_manager_phone: v4.contractualManagerPhone,
    hubspot_dealname: v4.hubspotDealname,
    hubspot_link: v4.hubspotLink,
    gdrive_link: v4.gdriveLink,
    created_at: v4.createdAt,
    updated_at: v4.updatedAt,
    created_by: v4.createdBy,
    // Account info (joined)
    account_id: v4.accountId,
    account_name: v4.accountName,
    account_number: v4.accountNumber,
    // MongoDB references
    mongo_quote_id: v4.mongoQuoteId,
  };
}

export async function fetchQuotes(params: QuotesQueryParams = {}): Promise<QuotesResponse> {
  const queryParams: Record<string, string | number | boolean> = {};

  if (params.search) queryParams.search = params.search;
  if (params.status) queryParams.status = params.status;
  if (params.status_in) queryParams.status_in = JSON.stringify(params.status_in);
  if (params.accountId) queryParams.accountId = params.accountId;
  if (params.pageNumber !== undefined) queryParams.pageNumber = params.pageNumber;
  if (params.pageLength !== undefined) queryParams.pageLength = params.pageLength;
  if (params.sortKey) queryParams.sortKey = params.sortKey;
  if (params.sortDirection !== undefined) queryParams.sortDirection = params.sortDirection;

  const response = await api.get<V4QuotesResponse>('/v4/quotes', queryParams);

  return {
    quotes: response.quotes.map(transformV4Quote),
    pageNumber: response.pageNumber,
    pageLength: response.pageLength,
    total: response.total,
  };
}

export async function fetchQuote(id: string): Promise<Quote> {
  const response = await api.get<V4Quote>(`/v4/quotes/${id}`);
  return transformV4Quote(response);
}

export async function fetchQuoteByNumber(quoteNumber: string): Promise<Quote> {
  const response = await api.get<V4Quote>(`/v4/quotes/by-number/${encodeURIComponent(quoteNumber)}`);
  return transformV4Quote(response);
}

export async function fetchQuoteSkus(quoteId: string): Promise<QuoteSku[]> {
  const response = await api.get<V4QuoteSkusResponse>(`/v4/quotes/${quoteId}/skus`);
  return response.skus;
}
